import React, { ReactNode, useContext, useEffect, useState } from "react";
import { MenuItem, MenuItemProps } from "./menu-item";
import { useTranslation } from "react-i18next";
import {
  SideBarMenuContextValue,
  SideBarMenuContext,
} from "./sidebar-menu-context-provider";
import "./sidebar-menu.css";
import { Spinner } from "@pokemon/design.ui.loading-spinner";

export interface SidebarMenuProps extends React.HTMLProps<HTMLDivElement> {
  /**
   * This is the react element that will be displayed at the top of the navegation bar menu
   */
  headerItem?: ReactNode;
  /**
   * Here we need to pass the Account Picker component to match the designs
   */
  accountPicker?: ReactNode;
  /**
   * This is an array of type MenuItemProps that contains all the menu options with their information
   */
  menuItems?: MenuItemProps[];
  /**
   * This is the react element that will be displayed at the bottom of the navegation bar menu
   */
  footerItem?: ReactNode;
  /**
   * We can use it to put our own className on the component
   */
  className?: string;
  /**
   * This prop is used internally to display the second bar you don't need to pass anything
   */
  mainMenuItem?: MenuItemProps;
  /**
   * Size of the screen where the nav bar will be displayed
   */
  screenSize?: string;
  /**
   * Function executed once the back button on the navegation bar is pressed
   */
  backButtonFn?: () => void;
  /**
   * Pass the uniqueId of the mainMenuItem element to display a loading spinner
   */
  mainMenuItemloading?: string;
}

export function Bar(props: SidebarMenuProps) {
  const { menuItems, headerItem, footerItem, accountPicker, mainMenuItem, backButtonFn, screenSize } = props;
  const { t } = useTranslation();
  const { selectedItemSubmenu, setSelectedItemMainMenu, selectedItemMainMenu, itemMainMenuWDataLoading } =
    useContext<SideBarMenuContextValue>(SideBarMenuContext);
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (mainMenuItem && itemMainMenuWDataLoading && itemMainMenuWDataLoading === mainMenuItem.uniqueId) {
      setLoading(true)
    } else {
      setLoading(false)
    }
  }, [itemMainMenuWDataLoading])

  const handleKeyDown = (event) => {
    if (
      event.key === "Escape" &&
      selectedItemSubmenu?.mainMenuItem &&
      selectedItemMainMenu?.uniqueId !== selectedItemSubmenu?.mainMenuItem?.uniqueId
    ) {
      setSelectedItemMainMenu(selectedItemSubmenu?.mainMenuItem);
    }
  };

  if (mainMenuItem) {
    return (
      <>
        {(!menuItems || loading) ? <div aria-busy={true} data-testid='pokemon-spinner' className={screenSize !== 'sm' ? "navbar-spinner-container" : "navbar-spinner-container-small"}><Spinner altText={t('pokemon_spinner', 'Loading')} /></div> :
          <div className={screenSize !== 'sm' ? "navbar-submenu-container" : "navbar-submenu-container-small"} onKeyDown={handleKeyDown}>
            {backButtonFn && <button data-testid='navbar-submenu-back-button' className="navbar-submenu-back-bttn" onClick={backButtonFn}><img className="navbar-submenu-back-bttn-icon" src='https://static.pokemon.com/images/back_button.svg' alt={t('back_button', 'Back')} />{t('main_menu', 'Main Menu')}</button>}
            <div className="navbar-submenu-name">
              <p>{mainMenuItem.name}</p>
            </div>
            <div className="navbar-menuitems-sub-container" role="menubar">
              {menuItems?.map((itemData) => (
                <MenuItem
                  uniqueId={itemData.uniqueId}
                  key={itemData.uniqueId}
                  name={itemData.name}
                  itemType={itemData.itemType}
                  iconSrc={itemData.iconSrc}
                  subMenuItems={itemData.subMenuItems}
                  to={itemData.to}
                  mainMenuItem={mainMenuItem}
                  screenSize={screenSize}
                />
              ))}
            </div>
          </div>
        }
      </>
    )
  } else {
    return (
      <div className={screenSize !== 'sm' ? "navbar-container" : "navbar-container-small"}>
        {headerItem && <div className={screenSize !== 'sm' ? "navbar-header" : "navbar-header-small"} >{headerItem}</div>}
        {accountPicker && (
          <div className="navbar-account-picker">{accountPicker}</div>
        )}
        <div className="navbar-menuitems-main-container" role="menubar">
          {menuItems?.map((itemData) => (
            <MenuItem
              uniqueId={itemData.uniqueId}
              key={itemData.uniqueId}
              name={itemData.name}
              itemType={itemData.itemType}
              iconSrc={itemData.iconSrc}
              subMenuItems={itemData.subMenuItems}
              to={itemData.to}
              screenSize={screenSize}
            />
          ))}
        </div>
        {footerItem && <div className="navbar-footer">{footerItem}</div>}
      </div>
    );
  }
}
